import * as React from "react";
import { format, isSameDay } from "date-fns";
import { Button, DatePicker, SearchableSelect } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { Namespace, useTypedT } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { IcCancel, IcPencil, IcPlus } from "@racoon/icons";
import { getEnumName } from "@racoon/lib";
import { fullName } from "~/shared/helpers";
import { ConsignmentNumberInput, FieldShell } from "~/shared/ui/consignment-inputs";
import {
  isTimeRangeInverted,
  type Agreement,
  type FieldSource,
  type RoutePatch,
  type RoutePatchKey,
  type ShiftPatch,
  type ShiftPatchKey,
  type SourceOption,
} from "../../hooks/ttn/ttnDraftUtils";
import type {
  RouteFieldAgreement,
  RouteFieldOptions,
  RouteFieldSources,
  ShiftFieldAgreement,
  ShiftFieldOptions,
  ShiftFieldSources,
  TripTimes,
} from "../../hooks/ttn/useTtnDraft";
import { SourceMenu, useSourceHint } from "./ttn-source";
import { ApproveButton, DocumentButtons } from "./shift-header";
import { getTripNumbers } from "../../utils/routeMetrics";

const NO_VALUE = "—";

const formatMetric = (value: number | undefined) =>
  value == null || Number.isNaN(value) ? NO_VALUE : String(Math.round(value * 100) / 100);

// A trip field's draft key: what its right-click pick or reset writes.
const ROUTE_PATCH_KEY = {
  start: "factStartAt",
  end: "factEndAt",
  place: "destination",
  weight: "weightKg",
  mileage: "mileage",
} as const satisfies Record<keyof RouteFieldOptions, RoutePatchKey>;

// Volume, weight and mileage columns: wide enough for a four-digit figure with
// two decimals next to its unit.
const METRIC_WIDTH = "w-[100px] shrink-0";

// ─── display primitives ───────────────────────────────────────────────────────

const FieldLabel: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <p
    className={`shrink-0 text-xs font-bold uppercase leading-4 text-ds-text-body ${className ?? "w-[100px]"}`}
  >
    {children}
  </p>
);

const Section: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <div className={`shrink-0 rounded-[12px] bg-ds-surface-secondary p-4 ${className ?? ""}`}>
    {children}
  </div>
);

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * The clock half of `SplitDateTimeField`: "HH : MM" as two inputs in one box.
 * A segment is committed once it is left — two digits typed move on by
 * themselves — keeping the day. Committing per keystroke would pad a first
 * digit straight back ("1" → "01") and swallow the second. Needs a day to
 * attach to, so it stays disabled until one is picked.
 */
const TimeField: React.FC<{
  value: Date | undefined;
  onChange: (next: Date) => void;
  disabled?: boolean;
  borderClassName?: string;
}> = ({ value, onChange, disabled, borderClassName }) => {
  const [hours, setHours] = React.useState(value ? pad2(value.getHours()) : "");
  const [minutes, setMinutes] = React.useState(value ? pad2(value.getMinutes()) : "");
  const minutesRef = React.useRef<HTMLInputElement>(null);
  const inactive = disabled || !value;

  React.useEffect(() => {
    setHours(value ? pad2(value.getHours()) : "");
    setMinutes(value ? pad2(value.getMinutes()) : "");
  }, [value]);

  const commit = (nextHours: string, nextMinutes: string) => {
    if (!value || !nextHours || !nextMinutes) return;
    // Compared to the minute shown: a GPS / scale time carries seconds, and
    // zeroing them on a mere focus and blur would read as an edit.
    if (Number(nextHours) === value.getHours() && Number(nextMinutes) === value.getMinutes())
      return;
    const next = new Date(value);
    next.setHours(Number(nextHours), Number(nextMinutes), 0, 0);
    onChange(next);
  };

  const digits = (raw: string) => raw.replace(/\D/g, "").slice(0, 2);
  const clamp = (raw: string, max: number) => (Number(raw) > max ? String(max) : raw);
  // A segment left empty falls back to the saved value; a single digit is padded.
  const settle = (raw: string, saved: number | undefined) =>
    raw ? pad2(Number(raw)) : saved != null ? pad2(saved) : "";

  const inputClass =
    "w-5 min-w-0 bg-transparent text-center text-sm tracking-[-0.01px] text-text-body outline-none placeholder:text-text-secondary disabled:cursor-not-allowed";

  return (
    <FieldShell
      className={`w-[80px] shrink-0 justify-center ${borderClassName ?? ""}`}
      disabled={inactive}
    >
      <input
        inputMode="numeric"
        disabled={inactive}
        value={hours}
        placeholder="--"
        aria-label="HH"
        onChange={(event) => {
          const raw = digits(event.target.value);
          setHours(clamp(raw, 23));
          if (raw.length === 2) minutesRef.current?.focus();
        }}
        // Read off the input: when two digits move focus on, this runs before
        // the typed value has reached state.
        onBlur={(event) => {
          const next = settle(clamp(digits(event.target.value), 23), value?.getHours());
          setHours(next);
          commit(next, settle(minutes, value?.getMinutes()));
        }}
        className={inputClass}
      />
      <span className="text-sm text-text-body">:</span>
      <input
        ref={minutesRef}
        inputMode="numeric"
        disabled={inactive}
        value={minutes}
        placeholder="--"
        aria-label="MM"
        onChange={(event) => {
          const raw = digits(event.target.value);
          setMinutes(clamp(raw, 59));
          if (raw.length === 2) minutesRef.current?.blur();
        }}
        onBlur={(event) => {
          const next = settle(clamp(digits(event.target.value), 59), value?.getMinutes());
          setMinutes(next);
          commit(settle(hours, value?.getHours()), next);
        }}
        className={inputClass}
      />
    </FieldShell>
  );
};

/**
 * Date + time as two boxes (Figma 16456:40726) — the day in the `DatePicker`,
 * the clock typed in beside it. One 202px width everywhere (departure / return,
 * trip start / unload), so the columns after it line up.
 */
const SplitDateTimeField: React.FC<{
  value: Date | undefined;
  onChange: (next: Date) => void;
  disabled?: boolean;
  className?: string;
  source?: FieldSource;
  agreement?: Agreement;
  /** Set when the value is invalid: red border, the message as its hint. */
  error?: string;
}> = ({ value, onChange, disabled, className, source, agreement, error }) => {
  const t = useTypedT(Namespace.Routes);
  const sourceHint = useSourceHint()(source, agreement);
  const hint = error ? { border: "!border-brand-red", title: error } : sourceHint;
  return (
    <div
      title={hint.title}
      className={`flex h-8 w-[202px] shrink-0 items-center gap-2 ${className ?? ""}`}
    >
      <DatePicker
        hideIcon
        value={value}
        disabled={disabled}
        placeholder={t("selectDate")}
        onChange={(day) => {
          // The day already shown is no edit, even with its seconds dropped.
          if (!day || (value && isSameDay(day, value))) return;
          // The picker hands back midnight; keep the clock already set.
          const next = new Date(day);
          if (value) next.setHours(value.getHours(), value.getMinutes(), 0, 0);
          onChange(next);
        }}
        className={`flex h-8 min-w-0 flex-1 items-center justify-start !px-3 !text-xs font-medium text-text-body ${hint.border}`}
      />
      <TimeField
        value={value}
        onChange={onChange}
        disabled={disabled}
        borderClassName={hint.border}
      />
    </div>
  );
};

/**
 * Location picker on the shared `SearchableSelect`, the location type on the
 * right of each row. Only active locations can be picked; the selected one is
 * looked up in the full list so a since-deactivated place still shows its name.
 */
const LocationPicker: React.FC<{
  id: string;
  depots: ConnectProto.Location[];
  selectedId: bigint | undefined;
  onSelect: (depot: ConnectProto.Location) => void;
  className?: string;
  disabled?: boolean;
  placeholder: string;
  searchPlaceholder: string;
  source?: FieldSource;
  agreement?: Agreement;
}> = ({
  id,
  depots,
  selectedId,
  onSelect,
  className,
  disabled,
  placeholder,
  searchPlaceholder,
  source,
  agreement,
}) => {
  const tEnum = useTypedT(Namespace.Enum);
  const hint = useSourceHint()(source, agreement);
  const options = React.useMemo(
    () =>
      depots
        .filter((depot) => depot.status === ConnectProto.LocationStatus.ACTIVE)
        .map((depot) => ({
          id: String(depot.id),
          name: depot.name,
          secondary: tEnum(
            getEnumName(ConnectProto.LocationTypeSchema, depot.type) ?? "LOCATION_TYPE_UNDEFINED",
          ),
        })),
    [depots, tEnum],
  );
  const selectedName = selectedId
    ? depots.find((depot) => depot.id === selectedId)?.name
    : undefined;

  return (
    <div title={hint.title} className={className ?? "min-w-0 flex-1"}>
      <SearchableSelect
        id={id}
        value={selectedId ? String(selectedId) : ""}
        options={options}
        valueLabel={selectedName}
        onChange={(next) => {
          const depot = depots.find((candidate) => String(candidate.id) === next);
          // Re-picking the place already shown is no edit.
          if (depot && depot.id !== selectedId) onSelect(depot);
        }}
        disabled={disabled}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        triggerClassName={`h-8 w-full rounded-lg px-2 ${hint.border}`}
      />
    </div>
  );
};

const formatUserName = (user: ConnectProto.User) =>
  fullName(user) || user.phoneNumber || `#${user.id}`;

/** Crew member picker on the shared `SearchableSelect`. */
const UserPicker: React.FC<{
  id: string;
  users: ConnectProto.User[];
  selected: ConnectProto.User | undefined;
  onSelect: (user: ConnectProto.User) => void;
  disabled?: boolean;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
}> = ({ id, users, selected, onSelect, disabled, placeholder, searchPlaceholder, emptyLabel }) => {
  const options = React.useMemo(
    () => users.map((user) => ({ id: String(user.id), name: formatUserName(user) })),
    [users],
  );

  return (
    <SearchableSelect
      id={id}
      value={selected ? String(selected.id) : ""}
      options={options}
      // The picked loader is already on the crew, so it is not among `users`.
      valueLabel={selected ? formatUserName(selected) : undefined}
      onChange={(next) => {
        const user = users.find((candidate) => String(candidate.id) === next);
        if (user) onSelect(user);
      }}
      disabled={disabled}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyLabel={emptyLabel}
      triggerClassName="h-8 w-[240px] shrink-0 rounded-lg px-2"
    />
  );
};

/**
 * Two-segment duration input rendered as a single field — "9 ч 47 мин".
 * Hours are unbounded (engine hours can exceed 24), minutes are clamped to 0–59,
 * and typing a second minute digit jumps focus out the same way time inputs do.
 */
const DurationField: React.FC<{
  /** Total duration in minutes, or null when unset. */
  value: number | null;
  onChange: (totalMinutes: number | null) => void;
  hourUnit: string;
  minuteUnit: string;
  disabled?: boolean;
  className?: string;
  title?: string;
}> = ({ value, onChange, hourUnit, minuteUnit, disabled, className, title }) => {
  const [hours, setHours] = React.useState(value == null ? "" : String(Math.floor(value / 60)));
  const [minutes, setMinutes] = React.useState(value == null ? "" : String(value % 60));
  const minutesRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setHours(value == null ? "" : String(Math.floor(value / 60)));
    setMinutes(value == null ? "" : String(value % 60));
  }, [value]);

  const commit = (nextHours: string, nextMinutes: string) => {
    if (!nextHours && !nextMinutes) {
      onChange(null);
      return;
    }
    const total = (Number(nextHours) || 0) * 60 + (Number(nextMinutes) || 0);
    onChange(total);
  };

  const digitsOnly = (raw: string, maxLength: number) => raw.replace(/\D/g, "").slice(0, maxLength);

  return (
    <FieldShell className={className} disabled={disabled} title={title}>
      <input
        inputMode="numeric"
        disabled={disabled}
        value={hours}
        placeholder="0"
        aria-label={hourUnit}
        onChange={(event) => {
          const next = digitsOnly(event.target.value, 4);
          setHours(next);
          commit(next, minutes);
        }}
        className="w-8 min-w-0 bg-transparent text-right text-sm text-text-primary outline-none placeholder:text-text-secondary disabled:cursor-not-allowed"
      />
      <span className="shrink-0 text-xs ">{hourUnit}</span>
      <input
        ref={minutesRef}
        inputMode="numeric"
        disabled={disabled}
        value={minutes}
        placeholder="00"
        aria-label={minuteUnit}
        onChange={(event) => {
          const raw = digitsOnly(event.target.value, 2);
          // 0–59 only; a leading digit above 5 is treated as the minutes value.
          const next = Number(raw) > 59 ? "59" : raw;
          setMinutes(next);
          commit(hours, next);
          if (raw.length === 2) minutesRef.current?.blur();
        }}
        onBlur={() => {
          if (minutes) setMinutes(String(Number(minutes)).padStart(2, "0"));
        }}
        className="w-8 min-w-0 bg-transparent text-right text-sm text-text-primary outline-none placeholder:text-text-secondary disabled:cursor-not-allowed"
      />
      <span className="shrink-0 text-xs ">{minuteUnit}</span>
    </FieldShell>
  );
};

/** A derived figure (volume, the way home): shown in the field box, never editable. */
const MetricBox: React.FC<{ value: string; unit?: string; className?: string }> = ({
  value,
  unit,
  className,
}) => (
  <FieldShell className={className} disabled title={value}>
    <span className="flex-1 truncate text-sm text-text-primary">{value}</span>
    {unit && <span className="shrink-0 text-xs text-text-secondary">{unit}</span>}
  </FieldShell>
);

// ─── main component ───────────────────────────────────────────────────────────

type Props = {
  /** Selected shift / its trips, with the TTN draft and fallbacks already applied. */
  shift: ConnectProto.DriverOnShift | undefined;
  routes: ConnectProto.Route[];
  /** Each trip's start / unload as shown: GPS / scale, actual or planned. */
  tripTimes: Map<bigint, TripTimes>;
  /** Where each shown value came from: the fields' border colours. */
  sources: { shift?: ShiftFieldSources; routes: Map<bigint, RouteFieldSources> };
  /** Once approved: what each stored value still agrees with — its border colour. */
  agreement: { shift?: ShiftFieldAgreement; routes: Map<bigint, RouteFieldAgreement> };
  /** Locations for the pickers, inactive ones included so saved places resolve. */
  depots: ConnectProto.Location[];
  /** Users that can be put on the shift's crew as loaders. */
  crewCandidates: ConnectProto.User[];
  disabled?: boolean;
  onShiftChange: (patch: ShiftPatch) => void;
  onCrewChange: (crewIds: bigint[]) => void;
  onRouteChange: (route: ConnectProto.Route, patch: RoutePatch) => void;
  /** What each field can be set to off its right-click menu. */
  options: { shift?: ShiftFieldOptions; routes: Map<bigint, RouteFieldOptions> };
  /** The fields holding an edit, so the menu offers to drop it. */
  edits: { shift: ShiftPatch; routes: Map<bigint, RoutePatch> };
  /** Mileage of the way home, last trip's end → shift return, off the track. */
  returnMileage?: number;
  /** Approve button; hidden when undefined, disabled while `blockedReason` is set. */
  approve?: {
    onClick: () => void;
    isLoading: boolean;
    hasUnsavedChanges: boolean;
    blockedReason?: string;
  };
  /**
   * Approved TTN: "Edit" while it is read-only; "Cancel" (drops the edits) and
   * `approve` as "Save" while it is being edited.
   */
  edit?: {
    isEditing: boolean;
    onStart: () => void;
    onCancel: () => void;
    disabled?: boolean;
  };
};

export const TtnRouteInfoContent: React.FC<Props> = ({
  shift,
  routes,
  tripTimes,
  sources,
  agreement,
  depots,
  crewCandidates,
  disabled: disabledProp,
  onShiftChange,
  onCrewChange,
  onRouteChange,
  options,
  edits,
  returnMileage,
  approve,
  edit,
}) => {
  const t = useTypedT(Namespace.Routes);
  const sourceHint = useSourceHint();

  // How each kind of value reads in the right-click menu.
  const formatTime = (value: SourceOption["value"]) =>
    value instanceof Date ? format(value, "dd.MM HH:mm") : NO_VALUE;
  const formatPlace = (value: SourceOption["value"]) =>
    depots.find((depot) => depot.id === value)?.name ?? NO_VALUE;
  const formatUnit = (unit: string) => (value: SourceOption["value"]) =>
    `${formatMetric(Number(value))} ${unit}`;
  const formatHours = (value: SourceOption["value"]) => {
    const minutes = Math.round(Number(value) * 60);
    return `${Math.floor(minutes / 60)} ${t("hourShort")} ${minutes % 60} ${t("minute")}`;
  };

  const shiftMenu = (
    key: ShiftPatchKey,
    formatValue: (value: SourceOption["value"]) => string,
  ) => ({
    options: options.shift?.[key],
    current: sources.shift?.[key],
    value:
      key === "departureTime" || key === "arrivalTime"
        ? (fromTimestamp(shift?.[key]) ?? undefined)
        : // A location id of 0 is no place; a zero figure is a value.
          shift?.[key] === 0n
          ? undefined
          : shift?.[key],
    isEdited: edits.shift[key] !== undefined,
    format: formatValue,
    onPick: (option: SourceOption) =>
      onShiftChange({ [key]: option.value, from: { [key]: option.source } } as ShiftPatch),
    // An `undefined` value drops the edit: the form fills the field in again.
    onReset: () => onShiftChange({ [key]: undefined }),
    disabled: disabledProp,
  });
  const routeMenu = (
    route: ConnectProto.Route,
    field: keyof RouteFieldOptions,
    formatValue: (value: SourceOption["value"]) => string,
  ) => {
    const key = ROUTE_PATCH_KEY[field];
    const times = tripTimes.get(route.id);
    const shownValue = {
      start: times?.start,
      end: times?.end,
      place: route.destinationLocationId || undefined,
      weight: route.weightKg,
      mileage: route.mileage,
    }[field];
    return {
      options: options.routes.get(route.id)?.[field],
      value: shownValue,
      current: sources.routes.get(route.id)?.[field],
      isEdited: edits.routes.get(route.id)?.[key] !== undefined,
      format: formatValue,
      onPick: (option: SourceOption) => {
        // The draft keeps the place as a location; the option names it by id.
        const value =
          key === "destination" ? depots.find((depot) => depot.id === option.value) : option.value;
        if (value === undefined) return;
        onRouteChange(route, { [key]: value, from: { [key]: option.source } } as RoutePatch);
      },
      onReset: () => onRouteChange(route, { [key]: undefined }),
      disabled: disabledProp,
    };
  };

  // "Add loader" opens empty rows; they join the crew once a user is picked.
  const [emptyLoaderSlots, setEmptyLoaderSlots] = React.useState(0);
  React.useEffect(() => setEmptyLoaderSlots(0), [shift?.id]);

  const crew = shift?.crew ?? [];
  const crewIds = crew.map((user) => user.id);
  const availableCrew = crewCandidates.filter(
    (user) => user.id !== shift?.driverId && !crewIds.includes(user.id),
  );
  // With no loaders there is still one empty row to pick into; it can't be removed.
  const hasDefaultLoaderRow = crew.length === 0;
  const loaderRows = [
    ...crew,
    ...Array<undefined>(emptyLoaderSlots + (hasDefaultLoaderRow ? 1 : 0)).fill(undefined),
  ];
  const tripNumbers = React.useMemo(() => getTripNumbers(routes), [routes]);

  // Trips whose start or unload is only the plan: approval leaves those unsaved.
  const planTimesCount = routes.filter((route) => {
    const times = tripTimes.get(route.id);
    return times?.startSource === "plan" || times?.endSource === "plan";
  }).length;

  // A shift without trips (none planned, or all cancelled) still gets its TTN.
  if (!shift) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-text-secondary">
        {t("ttnSelectRoute")}
      </div>
    );
  }

  const disabled = disabledProp;
  const isEditing = Boolean(edit?.isEditing);
  const arrivalAt = fromTimestamp(shift.arrivalTime) ?? undefined;
  // Shown the moment the times are in, not only once approval is tried.
  const shiftTimeError = isTimeRangeInverted({
    start: fromTimestamp(shift.departureTime) ?? undefined,
    end: arrivalAt,
  })
    ? t("ttnShiftDepartureAfterReturn")
    : undefined;
  const pickerLabels = {
    searchPlaceholder: t("search"),
    placeholder: t("ttnColUnloadPlace"),
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto">
      {/* ── Departure / return ─────────────────────────────────────────── */}
      <Section className="flex flex-col gap-2">
        {(
          [
            ["ttnDeparture", "departureTime", "departureLocationId"],
            ["ttnReturn", "arrivalTime", "arrivalLocationId"],
          ] as const
        ).map(([label, timeKey, locationKey]) => (
          <div className="flex h-8 items-center gap-4" key={label}>
            <FieldLabel>{t(label)}</FieldLabel>
            {/* 32px to the location per the mock: the row's 16px gap + mr-4. */}
            <SourceMenu {...shiftMenu(timeKey, formatTime)}>
              <SplitDateTimeField
                value={fromTimestamp(shift?.[timeKey]) ?? undefined}
                disabled={disabled}
                onChange={(date) => onShiftChange({ [timeKey]: date })}
                className="mr-4"
                source={sources.shift?.[timeKey]}
                agreement={agreement.shift?.[timeKey]}
                error={shiftTimeError}
              />
            </SourceMenu>
            <SourceMenu {...shiftMenu(locationKey, formatPlace)}>
              <LocationPicker
                id={`ttn-${locationKey}-search`}
                depots={depots}
                selectedId={shift?.[locationKey] || undefined}
                disabled={disabled}
                onSelect={(depot) => onShiftChange({ [locationKey]: depot.id })}
                source={sources.shift?.[locationKey]}
                agreement={agreement.shift?.[locationKey]}
                {...pickerLabels}
              />
            </SourceMenu>
          </div>
        ))}
        {shiftTimeError && <p className="text-xs text-ds-text-error">{shiftTimeError}</p>}
      </Section>

      {/* ── Engine hours + shift mileage ───────────────────────────────── */}
      <Section className="flex items-center justify-between overflow-x-auto w-auto">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-4">
            <FieldLabel>{t("ttnEngineHours")}</FieldLabel>
            {/* Stored as fractional hours, edited as hours + minutes. */}
            <SourceMenu {...shiftMenu("engineHours", formatHours)}>
              <DurationField
                value={shift?.engineHours != null ? Math.round(shift.engineHours * 60) : null}
                onChange={(minutes) =>
                  onShiftChange({ engineHours: minutes == null ? undefined : minutes / 60 })
                }
                hourUnit={t("hourShort")}
                minuteUnit={t("minute")}
                disabled={disabled}
                className={`w-[120px] shrink-0 ${sourceHint(sources.shift?.engineHours, agreement.shift?.engineHours).border}`}
                title={sourceHint(sources.shift?.engineHours, agreement.shift?.engineHours).title}
              />
            </SourceMenu>
          </div>
          <div className="flex items-center gap-4">
            <FieldLabel className="w-[116px]">{t("ttnShiftMileage")}</FieldLabel>
            {/* Defaults to the track over departure → return, following those times. */}
            <SourceMenu {...shiftMenu("mileage", formatUnit(t("km")))}>
              <ConsignmentNumberInput
                value={shift?.mileage ?? undefined}
                onCommit={(mileage) => onShiftChange({ mileage })}
                unit={t("km")}
                placeholder={NO_VALUE}
                disabled={disabled}
                className={`w-[120px] shrink-0 ${sourceHint(sources.shift?.mileage, agreement.shift?.mileage).border}`}
                title={sourceHint(sources.shift?.mileage, agreement.shift?.mileage).title}
              />
            </SourceMenu>
          </div>
        </div>
      </Section>

      {/* ── Loaders (shift crew) ───────────────────────────────────────── */}
      <Section className="flex items-end justify-between">
        <div className="flex flex-col gap-2">
          {loaderRows.map((user, index) => (
            <div className="flex h-8 items-center gap-4" key={user ? String(user.id) : index}>
              <FieldLabel>{t("ttnLoaderIndex", { index: index + 1 })}</FieldLabel>
              <div className="flex h-8 items-center gap-2">
                <UserPicker
                  id={`ttn-loader-search-${index}`}
                  users={availableCrew}
                  selected={user}
                  disabled={disabled}
                  onSelect={(next) => {
                    if (user) {
                      onCrewChange(crewIds.map((id) => (id === user.id ? next.id : id)));
                    } else {
                      // The first pick fills the default row, not an added one.
                      if (!hasDefaultLoaderRow) setEmptyLoaderSlots((count) => count - 1);
                      onCrewChange([...crewIds, next.id]);
                    }
                  }}
                  searchPlaceholder={t("search")}
                  emptyLabel={t("ttnLoadersNotFound")}
                  placeholder={t("ttnSelectLoader")}
                />
                {!(hasDefaultLoaderRow && index === 0) && (
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={t("ttnRemoveLoader")}
                    onClick={() =>
                      user
                        ? onCrewChange(crewIds.filter((id) => id !== user.id))
                        : setEmptyLoaderSlots((count) => count - 1)
                    }
                    className="disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <IcCancel className="h-6 w-6 text-ds-icon-error" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 text-ds-text-action hover:text-ds-text-action-hover"
          disabled={disabled}
          onClick={() => setEmptyLoaderSlots((count) => count + 1)}
        >
          <IcPlus className="h-5 w-5" />
          {t("ttnAddLoader")}
        </Button>
      </Section>

      {/* ── Trips table ────────────────────────────────────────────────── */}
      <Section className="flex flex-col gap-4 !rounded-xl overflow-x-auto w-auto">
        {routes.length === 0 ? (
          <p className="text-sm text-ds-text-secondary">{t("ttnNoTrips")}</p>
        ) : (
          <>
            <div className="flex items-center gap-4 text-xs font-bold uppercase leading-4 text-ds-text-secondary">
              <span className="w-[100px] shrink-0">{t("colRoute")}</span>
              <span className="w-[202px] shrink-0">{t("ttnColStartTime")}</span>
              <span className="w-[202px] shrink-0">{t("ttnColUnloadTime")}</span>
              <span className="w-[240px] shrink-0">{t("ttnColUnloadPlace")}</span>
              <span className={METRIC_WIDTH}>{t("colVolume")}</span>
              <span className={METRIC_WIDTH}>{t("ttnColWeight")}</span>
              <span className={METRIC_WIDTH}>{t("ttnColMileage")}</span>
            </div>

            <div className="flex flex-col gap-4">
              {routes.map((route) => {
                const routeSources = sources.routes.get(route.id) ?? {};
                const routeAgreement = agreement.routes.get(route.id) ?? {};
                const weightHint = sourceHint(routeSources.weight, routeAgreement.weight);
                const mileageHint = sourceHint(routeSources.mileage, routeAgreement.mileage);
                const timeError = isTimeRangeInverted(tripTimes.get(route.id))
                  ? t("ttnTripStartAfterUnloadField")
                  : undefined;
                return (
                  <div className="flex items-center gap-4" key={String(route.id)}>
                    <div className="flex w-[100px] shrink-0 items-center gap-4 whitespace-nowrap text-sm font-medium">
                      {/* A cancelled trip keeps only its id — it is not one of the numbered trips. */}
                      {tripNumbers.get(route.id) !== undefined && (
                        <span className="text-ds-text-action">P{tripNumbers.get(route.id)}</span>
                      )}
                      <span className="text-ds-text-body">M{String(route.id)}</span>
                    </div>
                    <SourceMenu {...routeMenu(route, "start", formatTime)}>
                      <SplitDateTimeField
                        value={tripTimes.get(route.id)?.start}
                        disabled={disabled}
                        onChange={(factStartAt) => onRouteChange(route, { factStartAt })}
                        source={routeSources.start}
                        agreement={routeAgreement.start}
                        error={timeError}
                      />
                    </SourceMenu>
                    <SourceMenu {...routeMenu(route, "end", formatTime)}>
                      <SplitDateTimeField
                        value={tripTimes.get(route.id)?.end}
                        disabled={disabled}
                        onChange={(factEndAt) => onRouteChange(route, { factEndAt })}
                        source={routeSources.end}
                        agreement={routeAgreement.end}
                        error={timeError}
                      />
                    </SourceMenu>
                    <SourceMenu {...routeMenu(route, "place", formatPlace)}>
                      <LocationPicker
                        id={`ttn-unload-depot-search-${route.id}`}
                        depots={depots}
                        selectedId={route.destinationLocationId || undefined}
                        disabled={disabled}
                        onSelect={(destination) => onRouteChange(route, { destination })}
                        className="w-[240px] shrink-0"
                        source={routeSources.place}
                        agreement={routeAgreement.place}
                        {...pickerLabels}
                      />
                    </SourceMenu>
                    {/* The jobs' fact volume, added up. */}
                    <MetricBox
                      value={formatMetric(route.volume)}
                      unit={t("volumeUnit")}
                      className={METRIC_WIDTH}
                    />
                    {/* The scale ticket fills it, but an edit wins over it. */}
                    <SourceMenu {...routeMenu(route, "weight", formatUnit(t("kg")))}>
                      <ConsignmentNumberInput
                        value={route.weightKg ?? undefined}
                        onCommit={(weightKg) => onRouteChange(route, { weightKg })}
                        unit={t("kg")}
                        placeholder={NO_VALUE}
                        disabled={disabled}
                        className={`${METRIC_WIDTH} ${weightHint.border}`}
                        title={weightHint.title}
                      />
                    </SourceMenu>
                    {/* Defaults to the track over the trip's start → unload. */}
                    <SourceMenu {...routeMenu(route, "mileage", formatUnit(t("km")))}>
                      <ConsignmentNumberInput
                        value={route.mileage ?? undefined}
                        onCommit={(mileage) => onRouteChange(route, { mileage })}
                        unit={t("km")}
                        placeholder={NO_VALUE}
                        disabled={disabled}
                        className={`${METRIC_WIDTH} ${mileageHint.border}`}
                        title={mileageHint.title}
                      />
                    </SourceMenu>
                    {timeError && (
                      <span className="whitespace-nowrap text-xs text-ds-text-error">
                        {timeError}
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Return leg — same field as the shift return above, no unload place. */}
              <div className="flex items-center gap-4">
                <FieldLabel>{t("ttnReturn")}</FieldLabel>
                {/* Under the unload time: the return is where the last leg ends. */}
                <div className="w-[202px] shrink-0" />
                <SourceMenu {...shiftMenu("arrivalTime", formatTime)}>
                  <SplitDateTimeField
                    value={arrivalAt}
                    disabled={disabled}
                    onChange={(arrivalTime) => onShiftChange({ arrivalTime })}
                    source={sources.shift?.arrivalTime}
                    agreement={agreement.shift?.arrivalTime}
                    error={shiftTimeError}
                  />
                </SourceMenu>
                <div className="w-[240px] shrink-0" />
                <div className={METRIC_WIDTH} />
                <div className={METRIC_WIDTH} />
                <MetricBox
                  value={formatMetric(returnMileage)}
                  unit={t("km")}
                  className={METRIC_WIDTH}
                />
              </div>
            </div>
          </>
        )}
      </Section>

      {/* ── Why approval is blocked / what it leaves out (the buttons are in the page header) ── */}
      {approve && (
        <div className="flex shrink-0 flex-col items-end gap-1 pb-2 text-xs">
          {approve.blockedReason ? (
            <span className="text-ds-text-error">{approve.blockedReason}</span>
          ) : (
            <>
              {planTimesCount > 0 && (
                <span className="flex items-center gap-1.5 text-ds-text-secondary">
                  <span className="h-4 w-4 shrink-0 rounded-[4px] border-2 border-ds-border-error" />
                  {t("ttnPlanTimesNotSaved", { count: planTimesCount })}
                </span>
              )}
              {approve.hasUnsavedChanges && (
                <span className="text-ds-text-secondary">
                  {t(isEditing ? "ttnEditUnsavedChanges" : "ttnUnsavedChanges")}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The TTN's header actions (Figma: Primary "Утвердить ТТН", print, Accent Green export):
 * approve — "Save" while an approved TTN is edited, with "Cancel" beside it — or "Edit"
 * once approved; print and download the approved document.
 */
export const TtnHeaderActions: React.FC<{
  approve?: Props["approve"];
  edit?: Props["edit"];
  /** Print / download the approved TTN; `disabledReason` until it can be. */
  document: {
    onPrint: () => void;
    onDownload: () => void;
    isLoading: boolean;
    disabledReason?: string;
  };
}> = ({ approve, edit, document }) => {
  const t = useTypedT(Namespace.Routes);
  const isEditing = Boolean(edit?.isEditing);
  return (
    <div className="flex items-center gap-2">
      {edit && !isEditing && (
        <Button
          id="ttn-edit-button"
          variant="dsSecondary"
          size="md"
          disabled={edit.disabled}
          onClick={edit.onStart}
        >
          <IcPencil className="h-4 w-4" />
          {t("ttnEdit")}
        </Button>
      )}
      {isEditing && edit && (
        <Button
          id="ttn-cancel-edit-button"
          variant="outline"
          size="md"
          disabled={edit.disabled}
          onClick={edit.onCancel}
        >
          {t("cancel")}
        </Button>
      )}
      {approve && (
        <ApproveButton
          id="ttn-approve-button"
          // Saving an edit with nothing changed would only rewrite the same TTN.
          disabled={approve.isLoading || (isEditing && !approve.hasUnsavedChanges)}
          reason={approve.blockedReason}
          onClick={approve.onClick}
        >
          {t(isEditing ? "save" : "approveTtn")}
        </ApproveButton>
      )}
      <DocumentButtons
        idPrefix="ttn"
        onPrint={document.onPrint}
        onDownload={document.onDownload}
        printLabel={t("print")}
        downloadLabel={t("downloadTtn")}
        disabled={document.isLoading}
        reason={document.disabledReason}
      />
    </div>
  );
};
